import { useMemo, useState } from "react";
import { Package2, PlusCircle } from "lucide-react";
import { Autocomplete, type AutocompleteOption } from "@/components/ui/autocomplete";
import { Button } from "@akuna/ui";
import { Badge } from "@/components/ui/badge";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import { Separator } from "@akuna/ui";
import type { PatientPackageSummary } from "@/hooks/usePatientPackages";
import { useTranslation } from "react-i18next";

interface ExistingPatientPackageSelectorProps {
  packages: PatientPackageSummary[];
  selectedPatientPackageIds: string[];
  isLoading?: boolean;
  disabled?: boolean;
  onAddPackage: (pkg: PatientPackageSummary) => void;
}

const formatCurrency = (value: number) =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export function ExistingPatientPackageSelector({
  packages,
  selectedPatientPackageIds,
  isLoading,
  disabled,
  onAddPackage,
}: ExistingPatientPackageSelectorProps) {
  const { t } = useTranslation("erp");
  const [search, setSearch] = useState("");
  const [selectedValue, setSelectedValue] = useState("");

  const availablePackages = useMemo(
    () => packages.filter((pkg) => !selectedPatientPackageIds.includes(pkg.id)),
    [packages, selectedPatientPackageIds],
  );

  const options = useMemo<AutocompleteOption[]>(
    () =>
      availablePackages.map((pkg) => ({
        value: pkg.id,
        label: pkg.catalog_package?.item?.name ?? t("common.package"),
      })),
    [availablePackages, t],
  );

  const selectedPackage = useMemo(
    () => availablePackages.find((pkg) => pkg.id === selectedValue) ?? null,
    [availablePackages, selectedValue],
  );

  const handleAdd = () => {
    if (!selectedPackage) return;
    onAddPackage(selectedPackage);
    setSearch("");
    setSelectedValue("");
  };

  const emptyMessage = disabled
    ? t("patientPackages.selectPatient")
    : availablePackages.length === 0
      ? t("patientPackages.emptyActive")
      : t("patientPackages.typeToSearch");

  if (disabled || packages.length === 0) {
    return null;
  }

  return (
    <>
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Package2 className="h-4 w-4 text-muted-foreground" />
          <div>
            <h3 className="text-sm font-medium">{t("patientPackages.title")}</h3>
            <p className="text-xs text-muted-foreground">
              {t("patientPackages.description")}
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : (
          <>
            <Autocomplete
              options={options}
              value={search}
              onChange={setSearch}
              onSelect={(option) => setSelectedValue(option.value)}
              selectedValue={selectedValue}
              disableLocalFilter={false}
              disabled={disabled}
              placeholder={t("patientPackages.placeholder")}
              emptyMessage={emptyMessage}
            />

            {selectedPackage ? (
              <div className="rounded-md border bg-muted/30 p-3 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium">
                    {selectedPackage.catalog_package?.item?.name ?? t("common.package")}
                  </span>
                  <Badge variant="secondary">
                    {selectedPackage.status_financeiro === "pago" ? t("patientPackages.paid") : t("patientPackages.open")}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground space-y-1">
                  <p>{t("patientPackages.sessionsRemaining", { count: selectedPackage.sessions_remaining })}</p>
                  <p>{t("patientPackages.outstandingBalance", { amount: formatCurrency(selectedPackage.outstanding_amount) })}</p>
                </div>
                <Button type="button" variant="outline" className="w-full wrap" onClick={handleAdd}>
                  <PlusCircle className="mr-2 h-4 w-4" />
                  {t("patientPackages.addToCart")}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
      <Separator />
    </>
  );
}
